<script setup lang="ts">
import { computed, nextTick, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowRight, ShieldCheck } from '@lucide/vue';
import { loginSchema } from '@evolfin/shared';
import { EvolfinApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useAltezzaAnimata } from '@/composables/altezzaAnimata';
import MarchioEvolfin from '@/components/app/MarchioEvolfin.vue';
import SfondoAmbiente from '@/components/app/SfondoAmbiente.vue';
import AvvisoErrore from '@/components/ui/AvvisoErrore.vue';
import CampoCheck from '@/components/ui/CampoCheck.vue';
import CampoTesto from '@/components/ui/CampoTesto.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';

// Il `meta` vive accanto alla vista che descrive, non in un elenco lontano.
// `pubblica` è ciò che tiene questa rotta fuori dal layout e dalla guardia.
definePage({
  meta: { pubblica: true, titolo: 'Accedi' },
});

/**
 * Il form valida con LO STESSO schema Zod usato dall'API: una regola sola,
 * due lati. È il pattern da replicare in ogni form.
 */
const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

const form = reactive({
  email: '',
  password: '',
  codiceTotp: '',
  codiceRecupero: '',
  ricordami: false,
});
const erroriCampo = ref<Record<string, string[]>>({});
const erroreGenerale = ref<string | null>(null);
const totpRichiesto = ref(false);
// Ripiego quando manca l'authenticator: si inserisce un codice di recupero.
const usaRecupero = ref(false);
const maiuscoleAttive = ref(false);
const campoTotp = ref<InstanceType<typeof CampoTesto> | null>(null);

// La card cresce e si accorcia in uso: errori di campo, blocco 2FA, avvisi.
const { riferimentoContenuto, altezza, animando } = useAltezzaAnimata();

const primoErrore = (chiave: string) => erroriCampo.value[chiave]?.[0];

// Chi ha perso una sessione aperta va avvisato, o penserebbe di aver sbagliato
// le credenziali. La guardia del router mette `expired` solo in quel caso: chi
// non ha mai fatto login o è appena uscito vede il form pulito.
const sessioneScaduta = computed(() => route.query.expired === '1');

async function invia(): Promise<void> {
  erroriCampo.value = {};
  erroreGenerale.value = null;

  const esito = loginSchema.safeParse({
    email: form.email,
    password: form.password,
    // Si invia l'uno o l'altro secondo factor, mai entrambi.
    codiceTotp: usaRecupero.value ? undefined : form.codiceTotp || undefined,
    codiceRecupero: usaRecupero.value ? form.codiceRecupero || undefined : undefined,
    ricordami: form.ricordami,
  });

  if (!esito.success) {
    for (const problema of esito.error.issues) {
      const chiave = problema.path.join('.');
      (erroriCampo.value[chiave] ??= []).push(problema.message);
    }
    return;
  }

  try {
    await auth.login(esito.data);
    await router.push((route.query.redirect as string) || '/dashboard');
  } catch (errore) {
    if (errore instanceof EvolfinApiError) {
      if (errore.dettagli.code === 'TOTP_RICHIESTO') {
        totpRichiesto.value = true;
        // Il campo appena comparso deve avere il fuoco: altrimenti l'utente
        // che ha già l'app aperta deve cercarlo col mouse.
        await nextTick();
        campoTotp.value?.focus();
        return;
      }
      if (errore.dettagli.code === 'TOTP_NON_VALIDO') {
        erroriCampo.value.codiceTotp = [errore.message];
        return;
      }
      if (errore.dettagli.code === 'CODICE_RECUPERO_NON_VALIDO') {
        erroriCampo.value.codiceRecupero = [errore.message];
        return;
      }
      erroriCampo.value = errore.erroriCampo;
      erroreGenerale.value = errore.message;
      return;
    }
    erroreGenerale.value = 'Errore imprevisto durante l’accesso. Riprova tra qualche istante.';
  }
}

/** Il blocco maiuscole è la causa più banale di password rifiutata. */
function verificaMaiuscole(evento: KeyboardEvent): void {
  maiuscoleAttive.value = evento.getModifierState?.('CapsLock') ?? false;
}
</script>

<template>
  <div class="relative grid min-h-screen place-items-center overflow-hidden px-4 py-12">
    <SfondoAmbiente />

    <main class="relative w-full max-w-[400px]">
      <!-- Intestazione-hero: è il picco della composizione. Il fuoco è il logo
           completo, che contiene già il nome: niente wordmark ripetuto sotto.
           "Accedi" arretra a occhiello: il colpo memorabile è l'identità, non
           il verbo. -->
      <header class="entra mb-8 flex flex-col items-center text-center">
        <div class="relative grid place-items-center">
          <span class="marchio-alone" aria-hidden="true"></span>
          <MarchioEvolfin :dimensione="128" class="relative" />
        </div>
        <h1 class="mt-5 text-[11.5px] font-semibold tracking-[0.16em] text-ink-secondary uppercase">
          Accedi al tool di gestione
        </h1>
      </header>

      <!-- Vetro e bordo vivo: la card focale sta sopra il mesh, è il caso
           in cui il vetro è concesso anche alla card. L'altezza cambia spesso (errori, 2FA): la
           transizione evita che ciò che sta sotto salti. -->
      <div class="entra card card--vetro bordo-riflesso p-7" style="animation-delay: 60ms">
        <div
          class="altezza-animata"
          :class="animando && 'altezza-animata--ritaglia'"
          :style="{ height: altezza }"
        >
          <form
            :ref="riferimentoContenuto"
            class="flex flex-col gap-4"
            novalidate
            @submit.prevent="invia"
          >
            <AvvisoErrore v-if="erroreGenerale" :messaggio="erroreGenerale" />
            <p
              v-else-if="sessioneScaduta"
              class="rounded-md bg-[var(--surface-accent-soft)] px-3 py-2.5 text-[13px] text-[var(--color-primary)]"
            >
              La sessione è scaduta. Accedi di nuovo per riprendere da dove eri.
            </p>

            <!-- I campi entrano dopo la card, in sequenza: l'occhio segue
               l'ordine in cui poi si compila. Il wrapper serve perché gli
               attributi di `CampoTesto` finiscono sull'`input`, non qui. -->
            <div class="entra" style="animation-delay: 150ms">
              <CampoTesto
                v-model="form.email"
                etichetta="Email"
                tipo="email"
                autocomplete="username"
                richiesto
                autofocus
                :errore="primoErrore('email')"
              />
            </div>

            <div class="entra" style="animation-delay: 210ms">
              <CampoTesto
                v-model="form.password"
                etichetta="Password"
                tipo="password"
                autocomplete="current-password"
                richiesto
                :errore="primoErrore('password')"
                :nota="maiuscoleAttive ? 'Blocco maiuscole attivo.' : undefined"
                @keyup="verificaMaiuscole"
                @keydown="verificaMaiuscole"
              />
            </div>

            <!-- Secondo fattore: compare solo quando il server lo chiede. -->
            <div
              v-if="totpRichiesto"
              class="entra flex flex-col gap-3 rounded-md border border-line-soft bg-soft p-3.5"
            >
              <p class="flex items-center gap-2 text-[12.5px] text-ink-secondary">
                <ShieldCheck
                  :size="15"
                  :stroke-width="1.8"
                  class="text-[var(--color-primary)]"
                  aria-hidden="true"
                />
                Questo account usa la verifica in due passaggi.
              </p>

              <CampoTesto
                v-if="!usaRecupero"
                ref="campoTotp"
                v-model="form.codiceTotp"
                etichetta="Codice di verifica"
                inputmode="numeric"
                autocomplete="one-time-code"
                :maxlength="6"
                tabulare
                :errore="primoErrore('codiceTotp')"
                nota="Le sei cifre generate dalla tua app di autenticazione."
              />
              <CampoTesto
                v-else
                v-model="form.codiceRecupero"
                etichetta="Codice di recupero"
                autocomplete="one-time-code"
                tabulare
                :errore="primoErrore('codiceRecupero')"
                nota="Uno dei codici salvati all'attivazione della 2FA."
              />

              <button
                type="button"
                class="self-start text-[12px] text-[var(--color-primary)] hover:underline"
                @click="usaRecupero = !usaRecupero"
              >
                {{
                  usaRecupero
                    ? 'Ho di nuovo il codice dell’app'
                    : 'Non hai il telefono? Usa un codice di recupero'
                }}
              </button>
            </div>

            <CampoCheck
              v-model="form.ricordami"
              etichetta="Ricordami su questo dispositivo"
              class="entra"
              style="animation-delay: 255ms"
            />

            <PulsanteAzione
              tipo="submit"
              larghezza-piena
              class="entra mt-1"
              style="animation-delay: 270ms"
              :caricamento="auth.caricamento"
            >
              {{ auth.caricamento ? 'Accesso in corso…' : 'Accedi' }}
              <template #icona>
                <ArrowRight :size="16" :stroke-width="2" aria-hidden="true" />
              </template>
            </PulsanteAzione>
          </form>
        </div>
      </div>

      <p class="entra mt-5 text-center text-[12.5px] text-ink-muted" style="animation-delay: 330ms">
        <RouterLink
          to="/auth/password-dimenticata"
          class="font-medium text-[var(--color-primary)] hover:underline"
        >
          Password dimenticata?
        </RouterLink>
      </p>
    </main>
  </div>
</template>

<style scoped>
/**
 * Alone del marchio: un bagliore smeraldo che "accende" il logo e lo rende il
 * primo fuoco della composizione. È accento del sistema, non un colore
 * nuovo — solo modulato in ampiezza. Decorativo, quindi fuori dal flusso.
 * Il logo ha il fondo trasparente: il bagliore è un anello attorno al
 * cerchio e lascia pulito l'interno, dove barre e scritta restano sul fondo.
 */
.marchio-alone {
  position: absolute;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  /* Alone sottile che accompagna l'anello del logo (raggio ~52% di questo
     box a 128px di logo): accenna la luce senza impastare il tratto. */
  background: radial-gradient(
    circle closest-side,
    transparent 47%,
    color-mix(in oklch, var(--color-accent) 18%, transparent) 53%,
    transparent 60%
  );
  filter: blur(6px);
  pointer-events: none;
}
</style>
